import { IsoDate } from 'modules/arena/reports/model/types';

// Datas do relatório são "datas de calendário" no timezone da arena. Toda a aritmética é
// feita em UTC sobre AAAA-MM-DD, então o fuso do navegador nunca desloca um dia.

const MS_PER_DAY = 86_400_000;

function toUtc(date: IsoDate): number {
  const [year, month, day] = date.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
}

function fromUtc(ms: number): IsoDate {
  return new Date(ms).toISOString().slice(0, 10);
}

/** "Hoje" no timezone do relatório (não no do navegador). */
export function todayIn(timezone: string, now: Date = new Date()): IsoDate {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

export function addDays(date: IsoDate, days: number): IsoDate {
  return fromUtc(toUtc(date) + days * MS_PER_DAY);
}

/** Dias entre as datas, contando as duas pontas (mesmo dia = 1). */
export function daysInclusive(start: IsoDate, end: IsoDate): number {
  return Math.round((toUtc(end) - toUtc(start)) / MS_PER_DAY) + 1;
}

/** 0 = domingo … 6 = sábado. */
export function weekdayOf(date: IsoDate): number {
  return new Date(toUtc(date)).getUTCDay();
}

export function startOfMonth(date: IsoDate): IsoDate {
  return `${date.slice(0, 7)}-01`;
}

export function isValidIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  return fromUtc(toUtc(value)) === value;
}

const SHORT = new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric' });
const LONG = new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' });
const DAY_MONTH = new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC', day: '2-digit', month: 'short' });

/** 12/09/2026 — formatação de data de calendário sem conversão de fuso. */
export function formatDate(date: IsoDate): string {
  return SHORT.format(new Date(toUtc(date)));
}

/** 12 de set. de 2026 */
export function formatDateLong(date: IsoDate): string {
  return LONG.format(new Date(toUtc(date)));
}

export function formatDayMonth(date: IsoDate): string {
  return DAY_MONTH.format(new Date(toUtc(date))).replace('.', '');
}

export function formatPeriod(start: IsoDate, end: IsoDate): string {
  return start === end ? formatDate(start) : `${formatDate(start)} a ${formatDate(end)}`;
}

/** Data e hora de um instante (ex.: última atualização) no timezone do relatório. */
export function formatTimestamp(isoTimestamp: string, timezone: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: timezone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(isoTimestamp));
}

export const WEEKDAY_LABELS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
export const WEEKDAY_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
